import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

import { JobAction } from '../../../action.types';
import { UserService } from '@intranet/modules/users/user.service';
import { LoginService } from '@intranet/modules/login';
import { PlannedBlockService } from '../planned-block.service';
import { PlannedBlockViewModel } from '../planned-block.types';
import { plannerColorForJobNumber } from '../planner-color.util';
import { formatDurationHM, formatTimeRange } from '../planner-date.util';
import {
    PlannedBlockDialogComponent,
    PlannedBlockDialogData,
    PlannedBlockDialogResult,
} from '../planned-block-dialog/planned-block-dialog.component';

export interface PlannedBlocksListDialogData {
    action: JobAction;
}

/** Whether any of the blocks was changed (moved, resized, removed or
 *  logged), so the list page can check the row's Planned flag again. */
export interface PlannedBlocksListDialogResult {
    changed: boolean;
}

interface BlockRow {
    block: PlannedBlockViewModel;
    start: Date;
    time: string;
    duration: string;
    personName: string;
    // Can open the edit popup: the user's own block, or anyone's with the
    // Planner role (the same rule as the Planner). Others are just listed.
    editable: boolean;
}

// An action's planned blocks, from "Planned blocks" in its ⋮ menu on the
// Actions list (spec: "a list of that Action's existing blocks, each
// opening the same edit popup as the planner"). Today on, soonest first;
// clicking one opens PlannedBlockDialogComponent over this, and the list
// reloads when that changed something. Same card look as "Add to planner"
// (its stylesheet is shared, see styleUrls).
@Component({
    selector: 'planned-blocks-list-dialog',
    templateUrl: './planned-blocks-list-dialog.component.html',
    styleUrls: [
        '../add-to-planner-dialog/add-to-planner-dialog.component.scss',
        './planned-blocks-list-dialog.component.scss',
    ],
})
export class PlannedBlocksListDialogComponent implements OnInit {
    readonly action: JobAction;
    rows: BlockRow[] = [];
    loading = true;
    failed = false;
    private _changed = false;

    constructor(
        @Inject(MAT_DIALOG_DATA) data: PlannedBlocksListDialogData,
        private _dialogRef: MatDialogRef<PlannedBlocksListDialogComponent, PlannedBlocksListDialogResult>,
        private _dialog: MatDialog,
        private _plannedBlockService: PlannedBlockService,
        private _userService: UserService,
        private _loginService: LoginService,
        private _changeRef: ChangeDetectorRef
    ) {
        this.action = data.action;
    }

    ngOnInit(): void {
        this.load();
    }

    get jobDotColor(): string {
        return plannerColorForJobNumber(this.action.jobNumber).titleColor;
    }

    get actionTitle(): string {
        const t = this.action.title || '';
        return t.charAt(0).toUpperCase() + t.slice(1);
    }

    private load(): void {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        this.loading = true;
        this.failed = false;
        this._plannedBlockService.getActionBlocks(this.action.jobActionId, today).subscribe({
            next: (blocks) => {
                this.rows = blocks.map((b) => this.toRow(b));
                this.loading = false;
                this._changeRef.markForCheck();
            },
            error: () => {
                this.loading = false;
                this.failed = true;
                this._changeRef.markForCheck();
            },
        });
    }

    private toRow(block: PlannedBlockViewModel): BlockRow {
        const start = new Date(block.start);
        const end = new Date(start.getTime() + block.durationMinutes * 60000);
        const user = (this._userService.users || []).find((u) => u.id === block.userId);
        return {
            block,
            start,
            time: formatTimeRange(start, end),
            duration: formatDurationHM(block.durationMinutes),
            personName: user ? user.fullName || `${user.firstName} ${user.surname}` : '',
            editable: block.userId === this._loginService.userId || this._loginService.checkAuth(['Planner']),
        };
    }

    trackRow(_: number, row: BlockRow): number {
        return row.block.plannedBlockId;
    }

    open(row: BlockRow): void {
        if (!row.editable) return;
        const ref = this._dialog.open<PlannedBlockDialogComponent, PlannedBlockDialogData, PlannedBlockDialogResult>(
            PlannedBlockDialogComponent,
            {
                panelClass: 'AddToPlannerDialog', // see vendor/_material.scss
                data: { action: this.action, block: row.block },
            }
        );
        ref.afterClosed().subscribe((result) => {
            if (!result?.changed) return;
            this._changed = true;
            this.load();
        });
    }

    close(): void {
        this._dialogRef.close({ changed: this._changed });
    }
}
